import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import PriceTable from '@/components/feature/PriceTable';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { REASSURANCE_ITEMS } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const PRICING_ROWS = [
  { service: 'Studio / 1 chambre (protocole complet 2-3 passages)', price: '90 € – 250 €' },
  { service: 'Logement 2-3 chambres', price: '200 € – 350 €' },
  { service: 'Hôtels / gîtes / locations saisonnières', price: 'Sur devis, intervention prioritaire' },
];

const FAQ_ITEMS = [
  {
    question: 'Comment ai-je attrapé des punaises de lit ?',
    answer: 'Voyage, hôtel, location, valise d\'un proche, meuble ou matelas d\'occasion : la punaise voyage avec les objets, pas avec la saleté. Une maison impeccable peut être infestée.',
  },
  {
    question: 'Puis-je m\'en débarrasser moi-même ?',
    answer: 'Les sprays grand public dispersent la colonie vers d\'autres pièces et renforcent les résistances. Le linge à 60 °C et l\'aspiration aident, mais sans traitement professionnel des caches, l\'infestation repart des œufs.',
  },
  {
    question: 'Combien de passages faut-il ?',
    answer: 'Deux au minimum, souvent trois : le premier élimine les adultes et larves, les suivants traitent les éclosions. C\'est inclus dans notre forfait — pas de facturation surprise au 2e passage.',
  },
  {
    question: 'Dois-je jeter mon matelas ?',
    answer: 'Presque jamais. La vapeur haute température assainit matelas et sommier ; une housse anti-punaises fait le reste. Jeter un matelas infesté sans précaution contamine d\'ailleurs l\'immeuble.',
  },
  {
    question: 'Combien de temps avant de dormir tranquille ?',
    answer: 'Les piqûres cessent généralement après le premier passage ; l\'éradication est confirmée en 3 à 6 semaines selon l\'ampleur.',
  },
];

export default function PunaisesDeLit() {
  useDocumentHead({
    title: 'Punaises de lit en Guadeloupe : traitement complet et discret',
    description: 'Piqûres au réveil, traces sur le matelas ? Traitement punaises de lit : protocole complet en 2-3 passages, discrétion totale. Devis gratuit en 2 h.',
    jsonLd: [service({ name: 'Traitement des punaises de lit', serviceType: 'Désinsectisation', path: '/punaises-de-lit-guadeloupe', description: 'Éradication complète des punaises de lit en Guadeloupe, protocole en 2-3 passages.' }), faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Punaises de lit', path: '/punaises-de-lit-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Punaises de lit'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/punaises-de-lit-1.webp"
            alt="Technicien inspectant les coutures d'un matelas à la recherche de punaises de lit dans une chambre en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Punaises de lit en Guadeloupe : le protocole qui les élimine vraiment
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Des piqûres alignées au réveil, des petites traces noires sur les coutures du matelas, des taches de sang sur les draps ? Les punaises de lit sont arrivées en Guadeloupe avec les voyages et le tourisme, et elles se propagent vite : valises, meubles d&rsquo;occasion, locations saisonnières. Une certitude : elles ne partent jamais seules, et les bombes insecticides aggravent le problème en les dispersant. Nos techniciens appliquent le protocole professionnel complet — et on n&rsquo;arrête pas tant qu&rsquo;elles ne sont pas toutes éliminées.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* COMMENT SAVOIR */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                  Comment savoir si ce sont des punaises de lit
                </h2>
                <ul className="space-y-3 text-foreground-600 text-base leading-relaxed">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span>Piqûres groupées ou alignées (souvent bras, jambes, dos), qui démangent fort</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span>Petites taches noires (déjections) sur les coutures du matelas, le sommier, les plinthes près du lit</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span>Traces de sang sur les draps</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span>Et dans les cas installés, punaises visibles (4-7 mm, brun-rouge, plates) dans les recoins du lit</span>
                  </li>
                </ul>
                <p className="text-foreground-700 font-semibold mt-5 text-base">
                  Le doute est fréquent avec les piqûres de moustiques — envoyez une photo, on tranche en 2 h.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-72 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="/images/punaises-de-lit-2.webp"
                    alt="Examen minutieux des coutures d'un matelas avec une lampe d'inspection en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* POURQUOI C'EST DIFFICILE */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Pourquoi c&rsquo;est l&rsquo;insecte le plus difficile à éliminer
              </h2>
              <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                <p>
                  La punaise se cache dans des fentes d&rsquo;un millimètre (sommier, plinthes, prises, cadres), pond des œufs résistants à la plupart des insecticides, peut jeûner des mois, et développe des résistances aux produits grand public.
                </p>
                <p>
                  Un traitement partiel = une réinfestation garantie 3 semaines plus tard, à l&rsquo;éclosion des œufs. C&rsquo;est pour ça que le protocole professionnel prévoit 2 à 3 passages espacés, pour éliminer les adultes PUIS les jeunes issus des œufs.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* NOTRE PROTOCOLE */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10 text-center">
              Notre protocole
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-10">
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  La vapeur sèche haute température tue les punaises ET les œufs, sans produit chimique sur le matelas. Combinée aux insecticides professionnels ciblés dans les zones de cache, c&rsquo;est le seul protocole qui garantit une éradication complète en 2 à 3 passages.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/punaises-de-lit-3.webp"
                    alt="Traitement à la vapeur d'un matelas infesté de punaises de lit dans une chambre en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Inspection complète
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Chambre(s) touchée(s) et pièces adjacentes — la punaise migre le long des plinthes et gaines.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Premier traitement
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Combinaison d&rsquo;insecticides professionnels ciblés (zones de cache) et de traitement mécanique (vapeur sèche haute température sur matelas, sommiers, coutures — tue punaises ET œufs sans produit).
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Passages de contrôle
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      À 2-3 semaines d&rsquo;intervalle, jusqu&rsquo;à éradication confirmée.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">4</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Accompagnement
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Consignes précises entre les passages (linge à 60 °C, aspiration, housses anti-punaises) — 50 % du succès se joue là, on vous guide pas à pas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-3xl mx-auto mt-6">
              <div className="bg-secondary-100/50 rounded-xl p-5 border border-secondary-200/50 text-center">
                <p className="text-foreground-700 text-sm leading-relaxed">
                  <strong>Discrétion absolue</strong> : intervention banalisée, aucun marquage.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs de traitement punaises de lit
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <PriceTable rows={PRICING_ROWS} />
              <div className="text-center mt-6">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* HÔTELS, GÎTES */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Hôtels, gîtes, locations : agissez en heures, pas en jours
              </h2>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center shrink-0 rounded-full bg-mango-500/20 mt-1">
                  <i className="ri-error-warning-line text-mango-500 text-lg w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                  <p>
                    Un avis client mentionnant des punaises peut plomber une saison entière. Nous intervenons en priorité pour les hébergeurs : traitement de la chambre touchée et des chambres mitoyennes, protocole de vérification, et plan de surveillance pour éviter la récidive.
                  </p>
                  <p className="font-semibold text-foreground-700">
                    Confidentialité contractuelle.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur les punaises de lit
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Retrouvez vos nuits. Protocole complet, discrétion totale."
      />

      {/* MAILLAGE INTERNE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/desinsectisation-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Tous nos traitements désinsectisation
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Quel que soit l&rsquo;insecte, on a le traitement
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Voir tous nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="punaises" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}